import { type RefObject, useCallback, useEffect, useRef, useState } from "react";
import "scrollyfills";

const DEFAULT_WIDTH = 100;

/**
 * Hook for horizontal scrolling with CSS only
 * @param {number} gap - gap in rem (default: 1rem)
 */
export const useSliderArrows = (containerRef: RefObject<HTMLDivElement>, gap = 1) => {
	const [canScrollNext, setCanScrollNext] = useState(true);
	const [canScrollPrev, setCanScrollPrev] = useState(false);
	const [isScrolling, setIsScrolling] = useState(false);
	const showArrows = useRef(false);

	const smallestWidth = useRef<number>(
		containerRef.current?.firstElementChild?.clientWidth ?? DEFAULT_WIDTH,
	);

	const stopScrolling = () => {
		setIsScrolling(false);
	};

	const checkForScroll = useCallback(() => {
		if (!containerRef.current) return;
		if (isScrolling) return;
		const container = containerRef.current;
		const _currentScroll = container.scrollLeft;
		const _remainingScroll = container.scrollWidth - container.clientWidth;
		setCanScrollNext(_remainingScroll - _currentScroll > smallestWidth.current);
		setCanScrollPrev(container.scrollLeft !== 0);

		if (!showArrows.current) {
			const slideCount = container.children.length;
			if (slideCount > 4) {
				showArrows.current = true;
			}
		}

		container.addEventListener("scrollend", stopScrolling, { once: true });
	}, [containerRef, isScrolling, stopScrolling]);

	useEffect(() => {
		if (!containerRef.current) return;
		const container = containerRef.current;

		container.addEventListener("scroll", checkForScroll);

		checkForScroll();

		return () => {
			container.removeEventListener("scroll", checkForScroll);
		};
	}, [containerRef, checkForScroll]);

	const scrollNext = () => {
		if (!containerRef?.current) return;
		if (isScrolling) return;
		const container = containerRef.current;
		const _slideWidth = container.firstElementChild?.clientWidth ?? DEFAULT_WIDTH;
		const scrollBy = _slideWidth + gap * 16;
		setIsScrolling(true);
		container.scrollBy({ left: scrollBy, behavior: "smooth" });
		checkForScroll();
	};

	const scrollPrev = () => {
		if (!containerRef.current) return;
		if (isScrolling) return;
		const container = containerRef.current;
		const _slideWidth = container.firstElementChild?.clientWidth ?? DEFAULT_WIDTH;
		const scrollBy = -1 * (_slideWidth + gap * 16);
		setIsScrolling(true);
		container.scrollBy({ left: scrollBy, behavior: "smooth" });
		checkForScroll();
	};

	return {
		containerRef,
		scrollNext,
		scrollPrev,
		canScrollNext,
		canScrollPrev,
		showArrows,
	};
};
